import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Subscription } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { baseqlMetaData } from '@adornis/baseql/metadata/metaDataStore.js';
import type { inheritanceTreeNode } from '@adornis/baseql/metadata/metaTree.js';
import { operationName } from '@adornis/baseql/symbols.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { FullDataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import type { ColumnDefinition } from '@adornis/data-view/ColumnDefinition.js';
import { convertColumnDefinitionsToSelectionSet } from '@adornis/data-view/convert-column-definitions.js';
import type { DataViewOperation } from '@adornis/data-view/data-view-operation.js';
import { View } from '@adornis/data-view/db/view.js';
import { getFieldFromEntity } from '@adornis/data-view/get-field-from-entity.js';
import { translateFieldName } from '@adornis/field-management/translate-field.js';
import type { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { subscribePaginatedAndFiltered } from '@adornis/filter/db/subscribePaginatedAndFiltered.js';
import { getStandardFilterOperation } from '@adornis/filter/get-standard-filter-operation.js';
import { ConvertConfig, FieldMappingEntry } from '@adornis/import-export/db/convert-config.js';
import { getBestEncodingForUserAgent } from '@adornis/import-export/encoding-helpers.js';
import { exportCSVFromEntities } from '@adornis/import-export/server/export-internals.js';
import { Language } from '@adornis/translation-core/languages.js';
import { Translator } from '@adornis/translation-core/translation.js';
import countries from '@chancenwerk/address/countries.json';
import { Partner } from '@chancenwerk/master-data-management/partner/entities/Partner.js';
import { Mitarbeiter } from '@chancenwerk/master-data-management/users/entities/Mitarbeiter.js';
import { VerwaltungsMitarbeiter } from '@chancenwerk/master-data-management/users/entities/VerwaltungsMitarbeiter.js';
import { DateTime } from 'luxon';
import { basename } from 'path';
import { Subject, firstValueFrom } from 'rxjs';
import { ChancenwerkUser } from '../master-data-management/users/entities/ChancenwerkUser.js';
import { ExternalContactPerson } from '../master-data-management/users/entities/ExternalContactPerson.js';
import { Student } from '../master-data-management/users/entities/Student.js';
import { translationData } from '../translation-data.js';

const isObject = value => ['object', 'function'].includes(typeof value) && value !== null;

// A "plain" object is an object who's a direct instance of Object
// (or, who has a null prototype).
const isPlainObject = value => {
  if (!isObject(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === null || prototype === Object.prototype;
};

// NOTE: This mutates `object`.
// It also may mutate anything that gets attached to `object` during the merge.
function merge(object, ...sources) {
  for (const source of sources) {
    for (const [key, value] of Object.entries(source)) {
      if (value === undefined) {
        continue;
      }

      // These checks are a week attempt at mimicking the various edge-case behaviors
      // that Lodash's `_.merge()` exhibits. Feel free to simplify and
      // remove checks that you don't need.
      if (!isPlainObject(value) && !Array.isArray(value)) {
        object[key] = value;
      } else if (Array.isArray(value) && !Array.isArray(object[key])) {
        object[key] = value;
      } else if (!isObject(object[key])) {
        object[key] = value;
      } else {
        merge(object[key], value);
      }
    }
  }

  return object;
}

function getOperationName<T extends typeof AdornisEntity>(fullOptions: FullDataViewOptions<T>) {
  const operation: string | DataViewOperation<T['prototype'], [any]> | undefined = fullOptions.operation;
  if (typeof operation === 'function') return operation[operationName];
  return operation;
}

const getExportFileName = (entityClassName: string) =>
  `Export_${entityClassName}_${DateTime.now().toFormat('yyyy-LL-dd-HH-mm')}`;

export const exportDataView = async <T extends typeof AdornisEntity>(
  dataViewOptions: FullDataViewOptions<T>,
  exportOptions?: { fileName?: string; encoding?: 'windows1252' | 'utf8'; stepSize?: number },
) => {
  const { XDialog } = await import('@adornis/dialog/x-dialog.js');
  const { downloadLink } = await import('@adornis/file-utils/client/helper-methods.js');
  return downloadLink(
    await XDialog.waitFor(
      CWExportDataView.cwExportDataView(
        dataViewOptions.class._class,
        getOperationName(dataViewOptions),
        dataViewOptions.view,
        exportOptions?.encoding ?? getBestEncodingForUserAgent(),
        exportOptions?.stepSize ?? 100,
      )(),
    ),
    exportOptions?.fileName ?? getExportFileName(dataViewOptions.class._class),
  );
};

const nodeExtendsEntity = <T extends typeof AdornisEntity>(node: inheritanceTreeNode<any>, classToExtend: T) => {
  let iterableNode: inheritanceTreeNode<any> | undefined = node;

  if ((iterableNode.type() as typeof AdornisEntity)._class === classToExtend._class) return true;
  while ((iterableNode = iterableNode.parent)) {
    if ((iterableNode.type() as typeof AdornisEntity)._class === classToExtend._class) return true;
  }
  return false;
};

const getSelectionSet = (entityClassName: string, entityClass: typeof AdornisEntity, view: View<any>) => {
  switch (entityClassName) {
    case ChancenwerkUser._class:
    case Student._class:
    case Mitarbeiter._class:
    case VerwaltungsMitarbeiter._class:
    case ExternalContactPerson._class:
      return merge(
        {
          // TODO: move this somewhere else
          studentID: 1,
          employeeID: 1,
          customBusinessAddressEnabled: 1,
          businessAddressPartnerID: 1,
          businessAddressExtra: 1,
          businessAddress: {
            partnerID: 1,
            partnerName: 1,
            address: {
              strasse: 1,
              careOf: 1,
              zipCity: {
                zip: 1,
                city: 1,
              },
              country: 1,
            },
          },
          customBusinessAddress: {
            strasse: 1,
            careOf: 1,
            zipCity: {
              zip: 1,
              city: 1,
            },
            country: 1,
          },
          kpfs: {
            kuerzel: 1,
            produkt: {
              kuerzel: 1,
            },
            school: {
              kuerzel: 1,
            },
          },
          personalSignOnChrstimasCardFrom: {
            vorname: 1,
            nachname: 1,
          },
        },
        convertColumnDefinitionsToSelectionSet<typeof AdornisEntity>(
          entityClass,
          (view.configuredColumns ?? []).filter(c => c.fieldName !== 'personalSignOnChrstimasCardFrom') as Readonly<
            ColumnDefinition<typeof AdornisEntity>[]
          >,
        ),
      );
    case Partner._class:
      return merge(
        {
          productStrings: 1,
        },
        convertColumnDefinitionsToSelectionSet<typeof AdornisEntity>(
          entityClass,
          (view.configuredColumns ?? []) as Readonly<ColumnDefinition<typeof AdornisEntity>[]>,
        ),
      );
    default:
      return convertColumnDefinitionsToSelectionSet<typeof AdornisEntity>(
        entityClass,
        (view.configuredColumns ?? []) as Readonly<ColumnDefinition<typeof AdornisEntity>[]>,
      );
  }
};

const getCustomFields = (entityClassName: string) => {
  switch (entityClassName) {
    case ChancenwerkUser._class:
    case Student._class:
    case Mitarbeiter._class:
    case VerwaltungsMitarbeiter._class:
    case ExternalContactPerson._class:
      return {
        customFields: {
          businessAddress: (entity: AdornisEntity) => {
            const businessAddress = (entity as ExternalContactPerson).businessAddress;
            if (!businessAddress) {
              return '';
            }

            const partnerName = businessAddress.partnerName ? `${businessAddress.partnerName}% ` : '';
            const extra = (entity as ExternalContactPerson).businessAddressExtra
              ? `${(entity as ExternalContactPerson).businessAddressExtra}% `
              : '';
            const strasse = businessAddress.address?.strasse ? `${businessAddress.address.strasse}% ` : '';
            const careOf = businessAddress.address?.careOf ? `${businessAddress.address.careOf}% ` : '';
            const zipCity = businessAddress.address?.zipCity
              ? `${businessAddress.address.zipCity.zip}% ${businessAddress.address.zipCity.city}% `
              : '';
            const country = countries[businessAddress.address?.country ?? ''] || '';

            const addressString = `${partnerName}${extra}${strasse}${careOf}${zipCity}${country}`
              .trim()
              .replaceAll('  ', ' ')
              .replaceAll(' %', '%')
              .replaceAll(' % ', '% ');
            if (addressString.includes('null')) {
              return '';
            }

            return addressString.endsWith('%') ? addressString.slice(0, -1) : addressString;
          },
          'adresse.country': (entity: AdornisEntity) => {
            const adresse = (entity as ChancenwerkUser).adresse;
            if (!adresse) return '';
            if (adresse.country) return countries[adresse.country];
            return '';
          },
          'attendanceStats.lastAttendance': (entity: AdornisEntity) => {
            if (entity instanceof Student) {
              return entity.attendanceStats?.lastAttendance?.toFormat('dd.MM.yyyy') ?? '';
            }
            return '';
          },
          personalSignOnChrstimasCardFrom: (entity: AdornisEntity) => {
            if (entity instanceof ChancenwerkUser) {
              return entity.personalSignOnChrstimasCardFrom?.map(c => c.toString()).join(', ') ?? '';
            }
            return '';
          },
        },
        customIDFields: {
          kpfsMultiple: (entity: AdornisEntity) => {
            if (entity instanceof Student) {
              return entity.kpfs?.map(kpf => kpf.kuerzel).join(', ') ?? '';
            }
            return '';
          },
          chancenID: (entity: AdornisEntity) => {
            return (entity as any).studentID ?? (entity as any).employeeID ?? '';
          },
        },
      };
    case Partner._class:
      return {
        customFields: {},
        customIDFields: {
          productStrings: (entity: AdornisEntity) => {
            if (entity instanceof Partner) {
              return entity.productStrings?.join(', ') ?? '';
            }
            return '';
          },
        },
      };
    default:
      return {
        customFields: {},
        customIDFields: {},
      };
  }
};

@Entity()
export class CWExportDataView extends AdornisEntity {
  static override _class = 'CWExportDataView';

  static _language = Language.GERMAN;
  static _translator = new Translator<typeof translationData>({
    dictionary: translationData,
    languages: [Language.GERMAN, Language.ENGLISH],
    getSelectedLanguage() {
      return CWExportDataView._language;
    },
    selectLanguage(lang) {
      CWExportDataView._language = lang;
    },
    onDictionaryUpdate() {},
  });

  static translateValue(value: any) {
    if (typeof value === 'number') value = value.toLocaleString(this._language === 'de' ? 'de-DE' : 'en-EN');
    return value;
  }

  @Subscription(type => String)
  static cwExportDataView(
    @Arg('entityClassName', type => String) entityClassName: string,
    @Arg('operationName', type => String) operationName: Maybe<string>,
    @Arg('view', type => View) view: View<any>,
    @Arg('encoding', type => String) encoding: string = 'utf8',
    @Arg('stepSize', type => Int) stepSize: number = 100,
  ) {
    return () => {
      const node = baseqlMetaData.entities.resolveNode(entityClassName);
      if (!nodeExtendsEntity(node, AdornisEntity)) throw new Error('Only accepts nodes that inherit AdornisEntity');

      const entityClass: typeof AdornisEntity = node.type() as typeof AdornisEntity;
      const operation = getStandardFilterOperation(
        entityClass,
        subscribePaginatedAndFiltered,
        operationName ?? undefined,
      );

      const selectionSet = getSelectionSet(entityClassName, entityClass, view);
      const subject = new Subject<string>();
      const entityIterable = getEntitiesForExport(operation, selectionSet, entityClass, view.filter, subject, stepSize);
      const { customFields, customIDFields } = getCustomFields(entityClassName);

      exportCSVFromEntities(
        new ConvertConfig({
          delimiter: ';',
          fieldMappings: (view.configuredColumns ?? [])
            .map(colDef => {
              if (!colDef.fieldName && !customIDFields[colDef.customID ?? '']) {
                return null;
              }

              return new FieldMappingEntry<string>(
                {
                  input: translateFieldName(colDef.fieldName ?? colDef.customID ?? '', this._translator),
                },
                () => null,
                (value, data) => {
                  try {
                    if (customIDFields[colDef.customID ?? '']) {
                      return customIDFields[colDef.customID ?? '']?.(data as AdornisEntity) ?? null;
                    }

                    if (customFields[colDef.fieldName ?? '']) {
                      return customFields[colDef.fieldName ?? '']?.(data as AdornisEntity) ?? null;
                    }

                    // @ts-expect-error siehe oben
                    let retreivedValue = getFieldFromEntity(data, colDef);

                    if (Array.isArray(retreivedValue)) {
                      retreivedValue = retreivedValue.map(v => this.translateValue(v));
                    } else {
                      retreivedValue = this.translateValue(retreivedValue);
                    }
                    return retreivedValue;
                  } catch (e) {
                    logger.warn({ err: e }, 'Error during export');
                    return 'ERROR';
                  }

                  return '';
                },
              );
            })
            .filter(Boolean) as FieldMappingEntry<string>[],
        }),
        entityIterable,
        getExportFileName(entityClassName),
        encoding,
      )
        .then(path => {
          subject.next(`/__export/download/${basename(path)}`);
          subject.complete();
        })
        .catch(e => {
          subject.error(e);
        });

      return subject;
    };
  }
}

async function* getEntitiesForExport<T extends AdornisEntity>(
  operation: DataViewOperation<T, [Maybe<AdornisFilter>]>,
  selectionSet: BaseQLSelectionSet<T>,
  entityClass: typeof AdornisEntity,
  filter: Maybe<AdornisFilter>,
  progressSubject: Subject<string>,
  stepSize: number,
) {
  let currentStep = 0;

  let lastEntities: any[] = await firstValueFrom(operation(currentStep, stepSize, filter)(selectionSet));

  do {
    progressSubject.next(`Collecting data... (${currentStep + lastEntities.length})`);
    for (const entity of lastEntities) {
      yield entity;
    }
    currentStep += stepSize;
  } while ((lastEntities = await firstValueFrom(operation(currentStep, stepSize, filter)(selectionSet))).length !== 0);
}
